import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { Separator } from "@/components/ui/separator";
import { SheetDescription } from "@/components/ui/sheet";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { usePromptLibrary } from "@/hooks/usePromptsLibrary";
import { useTranslation } from "@/hooks/useTranslation";
import { notifyPromptsChanged } from "@/lib/prompt-events";
import {
  getPromptLibraryCustomSectionsCount,
  getPromptLibraryNavigationItems,
} from "@/lib/prompt-library-navigation-items";
import { PinnedContext, PromptDTO, PromptOwnerType } from "@shaarp/common";
import { useCallback, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import DialogCreatePrompt from "./dialog-create-prompt";
import DialogEditPrompt from "./dialog-edit-prompt";
import { PromptLibraryContent } from "./prompt-library-content";
import { PromptLibraryHeader } from "./prompt-library-header";
import { PromptLibraryNavigation } from "./prompt-library-navigation";

type DialogSelectPromptProps = {
  onSelect: (prompt: PromptDTO) => void;
  onClose: () => void;
  context?: PinnedContext;
};

export default function DialogSelectPrompt({
  onSelect,
  onClose,
  context,
}: DialogSelectPromptProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();

  const [promptToEdit, setPromptToEdit] = useState<PromptDTO | null>(null);
  const [promptToDelete, setPromptToDelete] = useState<PromptDTO | null>(null);
  const [createOwnerType, setCreateOwnerType] =
    useState<PromptOwnerType | null>(null);

  const {
    setAllPrompts,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    filteredFavoritePrompts,
    filteredPinnedPrompts,
    filteredUserPrompts,
    filteredTeamPrompts,
    filteredCategories,
    publicPromptsByCategory,
    hasResults,
    handleToggleFavorite,
    handleTogglePinned,
    handleDeletePrompt,
    fetchAllPrompts,
    filterPrompts,
    favoriteIds,
    pinnedIds,
    PROMPT_CATEGORY_IDS,
  } = usePromptLibrary({ context });

  const handleSelectPrompt = useCallback(
    (prompt: PromptDTO) => {
      onSelect(prompt);
    },
    [onSelect],
  );

  const handleEditPrompt = useCallback((prompt: PromptDTO) => {
    setPromptToEdit(prompt);
  }, []);

  const handleRemovePrompt = useCallback((prompt: PromptDTO) => {
    setPromptToDelete(prompt);
  }, []);

  const confirmRemovePrompt = useCallback(async () => {
    if (!promptToDelete) return;
    await handleDeletePrompt(promptToDelete.id);
    setPromptToDelete(null);
  }, [promptToDelete, handleDeletePrompt]);

  const handleCreatePersonalPrompt = useCallback(() => {
    setCreateOwnerType("user");
  }, []);

  const handleCreateTeamPrompt = useCallback(() => {
    setCreateOwnerType("team");
  }, []);

  const handlePromptCreated = useCallback(
    async (newPrompt: PromptDTO) => {
      setAllPrompts((prev) => [...prev, newPrompt]);
      notifyPromptsChanged();
      setCreateOwnerType(null);
      if (context) {
        await handleTogglePinned(newPrompt);
        onClose();
      }
    },
    [setAllPrompts, context, handleTogglePinned, onClose],
  );

  const handlePromptSaved = useCallback(() => {
    fetchAllPrompts();
    // Notify other components about the updated prompt
    notifyPromptsChanged();
    setPromptToEdit(null);
  }, [fetchAllPrompts]);

  const navigationItems = useMemo(
    () =>
      getPromptLibraryNavigationItems({
        hasFavorites: filteredFavoritePrompts.length > 0,
        hasPinned: filteredPinnedPrompts.length > 0,
        filteredCategories,
        t,
      }),
    [filteredFavoritePrompts.length, filteredPinnedPrompts.length, filteredCategories, t],
  );

  const customSectionsCount = useMemo(
    () =>
      getPromptLibraryCustomSectionsCount(
        filteredFavoritePrompts.length > 0,
        filteredPinnedPrompts.length > 0,
      ),
    [filteredFavoritePrompts.length, filteredPinnedPrompts.length],
  );

  const libraryBody = (
    <>
      <PromptLibraryHeader
        pinnedCount={pinnedIds.size}
        favoriteCount={favoriteIds.size}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onCreateClick={handleCreatePersonalPrompt}
        isMobile={isMobile}
        context={context}
      />
      <Separator />
      {!isLoading && (
        <PromptLibraryNavigation
          items={navigationItems}
          customSectionsCount={customSectionsCount}
        />
      )}
      <div className="flex-1 px-6 py-6 space-y-10 overflow-y-auto">
        <PromptLibraryContent
          isLoading={isLoading}
          error={error}
          hasResults={hasResults}
          searchQuery={searchQuery}
          filteredFavoritePrompts={filteredFavoritePrompts}
          filteredPinnedPrompts={filteredPinnedPrompts}
          filteredUserPrompts={filteredUserPrompts}
          filteredTeamPrompts={filteredTeamPrompts}
          publicPromptsByCategory={publicPromptsByCategory}
          favoriteIds={favoriteIds}
          pinnedIds={pinnedIds}
          onSelectPrompt={handleSelectPrompt}
          onEditPrompt={handleEditPrompt}
          onRemovePrompt={handleRemovePrompt}
          onToggleFavorite={handleToggleFavorite}
          onTogglePinned={handleTogglePinned}
          onCreatePersonalPrompt={handleCreatePersonalPrompt}
          onCreateTeamPrompt={handleCreateTeamPrompt}
          filterPrompts={filterPrompts}
          PROMPT_CATEGORY_IDS={PROMPT_CATEGORY_IDS}
        />
      </div>
    </>
  );

  return (
    <>
      {isMobile ? (
        <MobileSheet
          open
          onOpenChange={(open) => {
            if (!open) onClose();
          }}
        >
          {createOwnerType ? (
            <DialogCreatePrompt
              variant="sheet"
              initialContent=""
              initialOwnerType={createOwnerType}
              onCreated={handlePromptCreated}
              onClose={() => setCreateOwnerType(null)}
            />
          ) : promptToEdit ? (
            <DialogEditPrompt
              variant="sheet"
              prompt={promptToEdit}
              onSaved={handlePromptSaved}
              onClose={() => setPromptToEdit(null)}
            />
          ) : (
            <>
              <SheetDescription className="sr-only">
                {t("prompt.library.description")}
              </SheetDescription>
              {libraryBody}
            </>
          )}
        </MobileSheet>
      ) : (
        <Dialog open={true} onOpenChange={onClose}>
          <DialogContent className="max-w-[1200px] max-h-[95vh] flex flex-col p-0 gap-0 overflow-hidden">
            {libraryBody}
            <Separator />
            <DialogFooter className="shrink-0 px-6 py-4 bg-muted/20">
              <DialogClose asChild>
                <Button variant="ghost">
                  {t("prompt.library.buttons.close")}
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {!isMobile && createOwnerType && (
        <DialogCreatePrompt
          initialContent=""
          initialOwnerType={createOwnerType}
          onCreated={handlePromptCreated}
          onClose={() => setCreateOwnerType(null)}
        />
      )}
      {!isMobile && promptToEdit && (
        <DialogEditPrompt
          prompt={promptToEdit}
          onSaved={handlePromptSaved}
          onClose={() => setPromptToEdit(null)}
        />
      )}
      <AlertDialog open={!!promptToDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("prompt.card.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("prompt.card.delete.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPromptToDelete(null)}>
              {t("prompt.card.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemovePrompt}>
              {t("prompt.card.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
